import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';

function riskBadge(rate) {
  if (rate >= 40) return <span className="rh-badge rh-badge-high">High</span>;
  if (rate >= 20) return <span className="rh-badge rh-badge-medium">Medium</span>;
  return <span className="rh-badge rh-badge-low">Low</span>;
}

function rateColor(rate) {
  if (rate >= 40) return '#dc2626';
  if (rate >= 20) return '#ca8a04';
  return '#16a34a';
}

function SubredditLabel({ subreddit }) {
  if (!subreddit || subreddit === 'unknown') {
    return (
      <span style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--rh-text-muted)', fontStyle: 'italic' }}>
        Unknown Subreddit
      </span>
    );
  }
  const label = subreddit.startsWith('r/') ? subreddit : `r/${subreddit}`;
  return <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{label}</span>;
}

/* ── Comment view + edit modal ───────────────────────────────────────────── */
function CommentModal({ comment, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [fields, setFields] = useState({
    publishing_date: comment.publishing_date || today,
    comment_text: comment.comment_text || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !saving) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const postHref = comment.reddit_url?.startsWith('http')
    ? comment.reddit_url
    : `https://www.reddit.com${comment.reddit_url || ''}`;

  const fieldLabel = {
    fontSize: 11, fontWeight: 700, color: 'var(--rh-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
  };
  const readBox = {
    background: 'var(--rh-surface, var(--rh-bg))', border: '1px solid var(--rh-border)',
    borderRadius: 8, padding: '10px 14px', fontSize: 14,
    color: 'var(--rh-text)', lineHeight: 1.55,
  };

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await redditApi.patch(`/admin/comments/${comment.id}/`, fields);
      onSaved(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div
      className="rh-modal-overlay"
      style={{ zIndex: 200, paddingLeft: '50vw', paddingRight: '2vw' }}
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <div className="rh-modal" style={{ width: '100%', maxWidth: 'min(560px, 46vw)' }}>
        <div className="rh-modal-header">
          <h3 className="rh-modal-title">View/Edit Comment</h3>
          <button className="rh-modal-close" onClick={() => !saving && onClose()}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSave}>
          <div className="rh-modal-body" style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            {error && <div className="rh-auth-error">{error}</div>}

            {/* Post title — read-only */}
            <div>
              <p style={fieldLabel}>Post</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--rh-text)' }}>
                {comment.post_title || '—'}
              </p>
            </div>

            {/* Thread URL — read-only */}
            <div>
              <p style={fieldLabel}>Reddit Thread URL</p>
              <div style={readBox}>
                <a href={postHref} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--rh-orange)', fontSize: 13, wordBreak: 'break-all', textDecoration: 'none' }}>
                  {postHref}
                  <svg style={{ marginLeft: 4, verticalAlign: 'middle' }} width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Schedule date — editable */}
            <div>
              <p style={fieldLabel}>Schedule Date</p>
              <input
                type="date"
                min={today}
                value={fields.publishing_date}
                onChange={(e) => setFields((f) => ({ ...f, publishing_date: e.target.value }))}
                required
                style={{
                  ...readBox, width: '100%', boxSizing: 'border-box',
                  cursor: 'pointer', outline: 'none',
                  fontFamily: 'inherit', fontSize: 14,
                  appearance: 'none', WebkitAppearance: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Comment text — editable */}
            <div>
              <p style={fieldLabel}>Comment Text</p>
              <textarea
                value={fields.comment_text}
                onChange={(e) => setFields((f) => ({ ...f, comment_text: e.target.value }))}
                rows={8}
                required
                style={{
                  ...readBox, width: '100%', boxSizing: 'border-box',
                  resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.65, outline: 'none',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--rh-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,69,0,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'var(--rh-border)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          <div className="rh-modal-footer" style={{ padding: '16px 28px', borderTop: '1px solid var(--rh-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="rh-btn-secondary" onClick={() => !saving && onClose()} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="rh-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/* ── Drawer ─────────────────────────────────────────────────────────────── */
function DrillDownDrawer({ subreddit, selectedClient, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const drawerRef = useRef(null);

  const fetchComments = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedClient) params.set('client', selectedClient);
    params.set('subreddit', subreddit || 'unknown');
    setLoading(true);
    redditApi.get(`/admin/comments/?${params.toString()}`)
      .then(({ data }) => setComments(data))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [subreddit, selectedClient]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !editingComment) onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, editingComment]);

  function handleSaved(updated) {
    setComments((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }

  const subLabel = (!subreddit || subreddit === 'unknown')
    ? 'Unknown Subreddit'
    : (subreddit.startsWith('r/') ? subreddit : `r/${subreddit}`);

  return createPortal(
    <>
      <div className="rh-drawer-overlay" onClick={onClose} />

      <aside className="rh-drawer" ref={drawerRef}>
        <div className="rh-drawer-header">
          <div>
            <h3 className="rh-drawer-title">{subLabel}</h3>
            {selectedClient && <p className="rh-drawer-subtitle">{selectedClient}</p>}
          </div>
          <button className="rh-drawer-close" onClick={onClose} title="Close (Esc)">×</button>
        </div>

        <div className="rh-drawer-body">
          <div className="rh-table-wrap">
            <table className="rh-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 140 }}>Post Title</th>
                  <th style={{ minWidth: 60 }}>URL</th>
                  <th style={{ minWidth: 140 }}>Comment</th>
                  <th style={{ whiteSpace: 'nowrap', minWidth: 100 }}>Schedule Date</th>
                  <th style={{ minWidth: 90 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="rh-empty">Loading…</td></tr>
                ) : comments.length === 0 ? (
                  <tr><td colSpan={5} className="rh-empty">No comments found.</td></tr>
                ) : comments.map((c) => {
                  const postHref = c.reddit_url?.startsWith('http')
                    ? c.reddit_url
                    : `https://www.reddit.com${c.reddit_url || ''}`;
                  return (
                    <tr key={c.id}>
                      <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 13, color: 'var(--rh-text)', fontWeight: 500 }} title={c.post_title}>
                          {c.post_title || '—'}
                        </span>
                      </td>

                      <td>
                        {c.reddit_url ? (
                          <a href={postHref} target="_blank" rel="noopener noreferrer" title={postHref}
                            style={{ color: 'var(--rh-orange)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            <span style={{ fontSize: 12 }}>Open</span>
                          </a>
                        ) : <span style={{ color: 'var(--rh-text-muted)', fontSize: 12 }}>—</span>}
                      </td>

                      <td style={{ maxWidth: 180 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, color: 'var(--rh-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {c.comment_text ? `${c.comment_text.slice(0, 35)}…` : '—'}
                          </span>
                          {c.comment_text && (
                            <button
                              onClick={() => setEditingComment(c)}
                              style={{ flexShrink: 0, background: 'none', border: '1px solid var(--rh-border)', borderRadius: 5, padding: '2px 8px', fontSize: 11, color: 'var(--rh-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              View
                            </button>
                          )}
                        </div>
                      </td>

                      <td className="rh-text-sm" style={{ whiteSpace: 'nowrap' }}>{c.publishing_date}</td>

                      <td>
                        <span className={`rh-badge rh-badge-${c.status}`}>{c.status === 'dws' ? 'Instant Deletion' : c.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </aside>

      {/* Edit/view modal — rendered above the drawer */}
      {editingComment && (
        <CommentModal
          comment={editingComment}
          onClose={() => setEditingComment(null)}
          onSaved={handleSaved}
        />
      )}
    </>,
    document.body,
  );
}

/* ── Main tab ────────────────────────────────────────────────────────────── */
export default function SubredditStatsTab({ stats, loading, selectedClient }) {
  const [selectedSubreddit, setSelectedSubreddit] = useState(null);
  const subreddits = stats?.subreddits || [];

  function handleRowClick(sub) {
    setSelectedSubreddit((prev) => (prev === sub.subreddit ? null : sub.subreddit));
  }

  return (
    <div className="rh-tab-scroll">
      <div className="rh-page-header">
        <div>
          <h2 className="rh-page-title">Subreddit Stats</h2>
          <p className="rh-page-subtitle">
            Which subreddits have the highest comment removal rates
            {selectedClient && <span style={{ color: 'var(--rh-orange)', marginLeft: 6 }}>· {selectedClient}</span>}
          </p>
        </div>
      </div>

      <div className="rh-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 20px', textAlign: 'right', borderBottom: '1px solid var(--rh-border)' }}>
          <span style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>
            Click a row to see comments for that subreddit
          </span>
        </div>
        <div className="rh-table-wrap rh-table-scroll">
        <table className="rh-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Subreddit</th>
              <th>Total Comments</th>
              <th>Deleted</th>
              <th>Deletion Rate</th>
              <th>Risk Level</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="rh-empty">Loading…</td></tr>
            ) : subreddits.length === 0 ? (
              <tr><td colSpan={5} className="rh-empty">No subreddit data yet.</td></tr>
            ) : subreddits.map((s) => {
              const isSelected = selectedSubreddit === s.subreddit;
              return (
                <tr
                  key={s.subreddit || '__unknown__'}
                  onClick={() => handleRowClick(s)}
                  style={{
                    cursor: 'pointer',
                    background: isSelected ? 'var(--rh-orange-light)' : undefined,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--rh-bg)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = ''; }}
                >
                  <td><SubredditLabel subreddit={s.subreddit} /></td>
                  <td>{s.total}</td>
                  <td style={{ color: '#dc2626' }}>{s.deleted}</td>
                  <td>
                    <span style={{ fontWeight: 700, color: rateColor(s.deletion_rate) }}>
                      {s.deletion_rate}%
                    </span>
                  </td>
                  <td>{riskBadge(s.deletion_rate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {selectedSubreddit !== null && (
        <DrillDownDrawer
          subreddit={selectedSubreddit}
          selectedClient={selectedClient}
          onClose={() => setSelectedSubreddit(null)}
        />
      )}
    </div>
  );
}
